import React from 'react';
import {
  Typography,
  Container,
  Box,
  Divider,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';

function TermsOfUse() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      <Container 
        maxWidth="md" 
        sx={{ 
          flex: 1,
          py: 8
        }}
      >
        <Typography 
          variant="h2" 
          component="h1"
          sx={{ 
            fontWeight: 700, 
            mb: 2,
            textAlign: 'center'
          }}
        >
          Terms of Use
        </Typography>
        
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: 'text.secondary' 
          }}
        >
          Last Updated: November 17, 2025
        </Typography>

        <Divider sx={{ mb: 6 }} />

        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Welcome to Actuation GmbH. These Terms of Use ("Terms") govern your access to and use of our website, 
            services, and any related content. By accessing or using our website, you agree to be bound by 
            these Terms. If you do not agree with any part of these Terms, please do not use our website or services.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Unless otherwise stated, the contents of this site, including but not limited to the text, images, 
            graphics, design, and arrangement, are the property of Actuation GmbH, all rights reserved.
          </Typography>
        </Box>

        {/* Use of Site Content */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Use of Site Content
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Actuation GmbH allows visitors to our website to read, print, or download one copy of information 
            (web pages, articles, PDF downloads) provided on our site for your personal, non-commercial use. 
            You may not reprint, modify, reproduce, or distribute any content without prior written permission 
            from Actuation GmbH.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Articles authored by Actuation GmbH and published by third-party publications are subject to the terms 
            and use of Actuation GmbH and/or the publisher. Users must obtain written permission prior to using, 
            reprinting, or republishing any such information.
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Permitted Uses
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Personal, non-commercial viewing and reading of website content
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Printing or downloading single copies for personal reference
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Sharing links to our website content through social media or email
            </Typography>
          </Box>

          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Prohibited Uses
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Reproducing, duplicating, or copying content for commercial purposes
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Modifying or creating derivative works from our content
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Removing or altering any copyright or proprietary notices
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Using our content in any way that infringes upon intellectual property rights
            </Typography>
          </Box>
        </Box>

        {/* Intellectual Property Rights */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Intellectual Property Rights
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            All content on this website, including but not limited to text, graphics, logos, images, audio 
            clips, digital downloads, data compilations, and software, is the property of Actuation GmbH or its 
            content suppliers and is protected by international copyright laws.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The compilation of all content on this site is the exclusive property of Actuation GmbH and is protected 
            by international copyright laws. All software used on this site is the property of Actuation GmbH or its 
            software suppliers and is protected by international copyright laws.
          </Typography>
        </Box>

        {/* Trademarks */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Trademarks
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            All trademarks on this website, including but not limited to "Actuation GmbH" and any associated logos, 
            are the property of their respective owners and may only be used with written permission of the owner. 
            You may not use any trademark, product name, or service name of Actuation GmbH without our prior written 
            permission.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Any unauthorized use of our trademarks or content may violate copyright laws, trademark laws, privacy 
            and publicity laws, and other regulations and statutes. Violators will be prosecuted to the fullest 
            extent of the law.
          </Typography>
        </Box>

        {/* User Responsibilities */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            User Responsibilities
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When using our website and services, you agree to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Provide accurate, current, and complete information when required
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Maintain the security and confidentiality of your account credentials
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Not engage in any activity that disrupts or interferes with our services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Not attempt to gain unauthorized access to our systems or networks
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Not use our services for any unlawful or prohibited purpose
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Comply with all applicable laws and regulations
            </Typography>
          </Box>
        </Box>

        {/* Links to Third-Party Sites */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Links to Third-Party Sites
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Actuation GmbH may provide links to third-party websites for your convenience and information. However, 
            we do not explicitly endorse the content and/or have control over the information supplied on these 
            external sites. The inclusion of any link does not imply endorsement by Actuation GmbH of the site or 
            any association with its operators.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Any use you make of the content provided by such third-party sites is at your own risk. We are not 
            responsible for the content, accuracy, or opinions expressed on these sites, and such sites are not 
            investigated, monitored, or checked for accuracy or completeness by us.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When you leave our website, you should be aware that our terms and policies no longer govern. You 
            should review the applicable terms and policies, including privacy and data gathering practices, of 
            any site to which you navigate from our website.
          </Typography>
        </Box>

        {/* Disclaimer */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Disclaimer
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The information provided on this site is free of charge and for informational purposes only and does 
            not create a business or professional services relationship between you and Actuation GmbH. The information 
            contained in this website is provided only as general information which may or may not reflect the most 
            current developments.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Accordingly, information on this website is not promised or guaranteed to be correct or complete. 
            Actuation GmbH expressly disclaims all liability in respect to actions taken or not taken based on any or 
            all the contents of this website.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>THE WEBSITE AND ALL CONTENT, MATERIALS, INFORMATION, SERVICES, AND SOFTWARE ARE PROVIDED 
            "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong>
          </Typography>
        </Box>

        {/* Limitation of Liability */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To the fullest extent permitted by applicable law, in no event shall Actuation GmbH, its affiliates, 
            directors, employees, or agents be liable for any indirect, incidental, special, consequential, or 
            punitive damages, including without limitation, loss of profits, data, use, goodwill, or other 
            intangible losses, resulting from:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Your access to or use of or inability to access or use the website or services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Any conduct or content of any third party on the website
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Any content obtained from the website
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Unauthorized access, use, or alteration of your transmissions or content
            </Typography>
          </Box>
        </Box>

        {/* Indemnification */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Indemnification
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You agree to defend, indemnify, and hold harmless Actuation GmbH, its affiliates, licensors, and service 
            providers, and its and their respective officers, directors, employees, contractors, agents, licensors, 
            suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, 
            losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to 
            your violation of these Terms or your use of the website.
          </Typography>
        </Box>

        {/* Termination */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Termination
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may terminate or suspend your access to our website and services immediately, without prior notice 
            or liability, for any reason whatsoever, including without limitation if you breach these Terms.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Upon termination, your right to use the website will immediately cease. All provisions of these Terms 
            which by their nature should survive termination shall survive, including without limitation ownership 
            provisions, warranty disclaimers, indemnity, and limitations of liability.
          </Typography>
        </Box>

        {/* Governing Law */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Governing Law and Jurisdiction
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in 
            which Actuation GmbH operates, without regard to its conflict of law provisions.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Any disputes arising out of or relating to these Terms or the use of our website shall be resolved 
            exclusively in the courts of the applicable jurisdiction, and you consent to the personal jurisdiction 
            of such courts.
          </Typography>
        </Box>

        {/* Changes to Terms */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Changes to These Terms
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision 
            is material, we will provide at least 30 days' notice prior to any new terms taking effect. What 
            constitutes a material change will be determined at our sole discretion.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By continuing to access or use our website after any revisions become effective, you agree to be bound 
            by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the 
            website.
          </Typography>
        </Box>

        {/* Severability */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Severability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed 
            and interpreted to accomplish the objectives of such provision to the greatest extent possible under 
            applicable law, and the remaining provisions will continue in full force and effect.
          </Typography>
        </Box>

        {/* Entire Agreement */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Entire Agreement
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms, together with our Privacy Policy and any other legal notices published by us on the 
            website, shall constitute the entire agreement between you and Actuation GmbH concerning the use of our 
            website and services.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Contact Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you have any questions about these Terms of Use, please contact us:
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Actuation GmbH</strong><br />
            Heinrichstrasse 118<br />
            8005 Zürich<br />
            Switzerland<br />
            UID: CHE-260.133.410<br />
            Email: <a href="mailto:enquiries@actuation.ch" style={{ color: 'inherit' }}>enquiries@actuation.ch</a>
          </Typography>
        </Box>

        <Divider sx={{ my: 6 }} />

        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          By using our website and services, you acknowledge that you have read, understood, and agree to be 
          bound by these Terms of Use.
        </Typography>
      </Container>

      <Footer />
    </Box>
  );
}

export default TermsOfUse;

